import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  QUESTIONS,
  computeResults,
  computeTopDrivers,
  computeRecoveryDebt,
  computeAttentionLeak,
  computeTimeReclaimed,
  computeLeanWorkweek,
  computeTeamSpillover,
  ARCHETYPES,
} from '../ctfd/quizLogic';
import ArchetypeIcon from '../ctfd/ArchetypeIcon';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// =============================================
// SECTION CARD COMPONENT (sharper version)
// =============================================
function SectionCard({ name, score, label, insight }) {
  const labelColor = label === 'Contained' ? '#4CAF50' : label === 'Compounding' ? '#FF9800' : label === 'Expensive' ? '#E8590C' : '#D32F2F';
  const parsed = typeof insight === 'object' ? insight : null;
  const legacyText = typeof insight === 'string' ? insight : null;
  return (
    <div className="stq-section-card" data-testid={`section-${name.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="stq-section-card-header">
        <span className="stq-section-name">{name}</span>
        <span className="stq-section-score-pill" style={{ background: labelColor }}>{score} &middot; {label}</span>
      </div>
      <div className="stq-section-bar-track"><div className="stq-section-bar-fill" style={{ width: `${Math.min(score, 100)}%`, background: labelColor }} /></div>
      {parsed ? (
        <>
          {parsed.diagnosis && <p className="stq-card-diagnosis">{parsed.diagnosis}</p>}
          {parsed.evidence && <p className="stq-card-evidence">{parsed.evidence}</p>}
          {parsed.cost && <p className="stq-card-cost">{parsed.cost}</p>}
        </>
      ) : legacyText ? (
        <p className="stq-section-insight">{legacyText}</p>
      ) : null}
    </div>
  );
}

// =============================================
// MAIN QUIZ COMPONENT
// =============================================
export default function CTFDQuiz() {
  const navigate = useNavigate();
  const [screen, setScreen] = useState('intro');
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [downstreamInput, setDownstreamInput] = useState('');
  const [downstreamError, setDownstreamError] = useState('');
  const [personalTime, setPersonalTime] = useState('');
  const [personalTimeError, setPersonalTimeError] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [nameError, setNameError] = useState('');
  const [profileUrl, setProfileUrl] = useState('');
  const [profileUrlError, setProfileUrlError] = useState('');
  const [results, setResults] = useState(null);
  const [derived, setDerived] = useState(null);
  const [insights, setInsights] = useState(null);
  const [quizId, setQuizId] = useState(null);
  const [fadeKey, setFadeKey] = useState(0);
  const [loadingStep, setLoadingStep] = useState(0);

  const LOADING_STEPS = [
    { main: 'Reading your responses...', sub: 'Organizing your inputs across workload, recovery, attention, and boundaries' },
    { main: 'Mapping your operating pattern...', sub: 'Looking for the dominant pattern behind your current pace' },
    { main: 'Scoring your Success Tax...', sub: 'Calculating the overall cost of your current operating style' },
    { main: 'Measuring attention leak...', sub: 'Estimating the hidden time lost to switching, re-entry, and reactive drag' },
    { main: 'Estimating recovery debt...', sub: 'Comparing your workload against sustainable recovery needs' },
    { main: 'Assessing downstream impact...', sub: 'Modeling how your pace may be affecting the people around you' },
    { main: 'Preparing your results...', sub: "Turning the diagnostic into a clear read on what it's costing you and what you could get back" },
  ];

  useEffect(() => {
    if (screen !== 'loading') { setLoadingStep(0); return; }
    setLoadingStep(0);
    const id = setInterval(() => {
      setLoadingStep(s => (s < LOADING_STEPS.length - 1 ? s + 1 : s));
    }, 2500);
    return () => clearInterval(id);
  }, [screen]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const el = document.querySelector('.stq-page');
    if (el) el.scrollTop = 0;
  }, [screen, currentQ]);

  const advanceFromQ = (qi) => {
    setTimeout(() => {
      if (qi < 14) { setFadeKey(k => k + 1); setCurrentQ(qi + 1); }
      else { setFadeKey(k => k + 1); setScreen('q16'); }
    }, 300);
  };

  const selectAnswer = (qi, ai) => {
    setAnswers(p => ({ ...p, [qi]: ai }));
    advanceFromQ(qi);
  };

  const submitDownstream = () => {
    const trimmed = downstreamInput.trim();
    if (trimmed === '') { setDownstreamError('Enter a number (0 if none)'); return; }
    const n = Number(trimmed);
    if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) { setDownstreamError('Enter a valid whole number'); return; }
    if (n > 100000) { setDownstreamError('That number seems too high'); return; }
    setDownstreamError('');
    setAnswers(p => ({ ...p, 3: n }));
    advanceFromQ(3);
  };

  const goBack = () => {
    if (currentQ > 0) {
      setFadeKey(k => k + 1);
      const prevQ = currentQ - 1;
      setCurrentQ(prevQ);
      if (QUESTIONS[prevQ].type === 'numeric') {
        setDownstreamInput(answers[prevQ] !== undefined ? String(answers[prevQ]) : '');
        setDownstreamError('');
      }
    } else setScreen('intro');
  };

  const submitQuiz = async () => {
    if (!firstName.trim() || !lastName.trim()) { setNameError('First and last name are required'); return; }
    if (!email.trim()) { setEmailError('Email is required to see your results'); return; }
    if (!/\S+@\S+\.\S+/.test(email)) { setEmailError('Enter a valid email'); return; }
    const url = profileUrl.trim();
    if (!url) { setProfileUrlError('Website or LinkedIn URL is required'); return; }
    if (!/\.[a-z]{2,}/i.test(url) || url.length < 6) { setProfileUrlError('Enter a valid website or LinkedIn URL'); return; }
    setNameError(''); setEmailError(''); setProfileUrlError('');

    setScreen('loading');
    const res = computeResults(answers);
    setResults(res);

    const topDrivers = computeTopDrivers(res.rawScores, res.rawAnswers);
    const recoveryDebt = computeRecoveryDebt(answers);
    const attentionLeak = computeAttentionLeak(answers);
    const timeReclaimed = computeTimeReclaimed(attentionLeak.displayRange);
    const leanWorkweek = computeLeanWorkweek(answers, timeReclaimed);
    const teamSpillover = computeTeamSpillover(answers, res.sectionLabels.leadershipSpillover, attentionLeak.displayRange);
    setDerived({ topDrivers, recoveryDebt, attentionLeak, timeReclaimed, leanWorkweek, teamSpillover });

    try {
      const resp = await fetch(`${API_URL}/api/ctfd/quiz/submit`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          raw_answers: res.rawAnswers,
          raw_scores: res.rawScores,
          section_scores: res.sectionScores,
          section_labels: res.sectionLabels,
          overall_score: res.overallScore,
          overall_band: res.overallBand,
          archetype: res.archetype,
          downstream_impact: res.downstreamImpact,
          email: email.trim(),
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          profile_url: profileUrl.trim(),
          personal_time_answer: personalTime.trim(),
          downstream_count: Number(answers[3]) || 0,
          top_drivers: topDrivers,
          recovery_debt: recoveryDebt,
          attention_leak: attentionLeak,
          time_reclaimed: timeReclaimed,
          lean_workweek: leanWorkweek,
          team_spillover: teamSpillover,
        })
      });
      const data = await resp.json();
      setQuizId(data.id);
      setInsights(data.insights);
    } catch {
      // Use results without AI insights
    }
    setScreen('results');
  };

  const trackApply = async () => {
    if (quizId) {
      fetch(`${API_URL}/api/ctfd/quiz/${quizId}/clicked-apply`, { method: 'PATCH' }).catch(() => {});
    }
    navigate('/ctfdprotocol');
  };

  // =============================================
  // RENDER
  // =============================================
  return (
    <div className="stq-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&family=Instrument+Serif:ital@0;1&family=Caveat:wght@400;500;600;700&display=swap');

        .stq-page {
          --stq-bg: #FAFAF8;
          --stq-text: #1A1A1A;
          --stq-mid: #555;
          --stq-light: #888;
          --stq-accent: #E8590C;
          --stq-card: #F2F1EE;
          --stq-border: #E0DFDB;
          --stq-serif: 'Instrument Serif', Georgia, serif;
          --stq-sans: 'DM Sans', -apple-system, sans-serif;
          --stq-hand: 'Caveat', cursive;

          position: fixed;
          inset: 0;
          overflow-y: auto;
          overflow-x: hidden;
          z-index: 9999;
          font-family: var(--stq-sans) !important;
          font-size: 17px;
          line-height: 1.7;
          color: var(--stq-text);
          background: var(--stq-bg);
          -webkit-font-smoothing: antialiased;
          -webkit-overflow-scrolling: touch;
        }
        .stq-page *, .stq-page *::before, .stq-page *::after { box-sizing: border-box; margin: 0; padding: 0; }

        .stq-container { max-width: 680px; margin: 0 auto; padding: 0 24px; }
        @media (max-width: 600px) { .stq-container { padding: 0 20px; } }

        /* PROGRESS BAR */
        .stq-progress { position: sticky; top: 0; z-index: 10; background: var(--stq-bg); padding: 16px 0 8px; }
        .stq-progress-track { height: 3px; background: var(--stq-border); border-radius: 2px; overflow: hidden; }
        .stq-progress-fill { height: 100%; background: var(--stq-accent); transition: width 0.4s ease; border-radius: 2px; }
        .stq-progress-label { font-size: 0.75rem; font-weight: 600; letter-spacing: 0.1em; color: var(--stq-light); margin-top: 6px; text-transform: uppercase; }

        /* SCREENS */
        .stq-screen { min-height: 80vh; display: flex; flex-direction: column; justify-content: center; padding: 48px 0; }
        @media (max-width: 600px) { .stq-screen { padding: 32px 0; min-height: 70vh; } }

        /* INTRO */
        .stq-intro-label { font-size: 0.78rem; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; color: var(--stq-accent); margin-bottom: 16px; }
        .stq-intro h1 { font-family: var(--stq-sans) !important; font-size: clamp(2.4rem, 8vw, 3.8rem); font-weight: 700; line-height: 1.05; letter-spacing: -0.03em; margin-bottom: 8px; }
        .stq-intro h2 { font-family: var(--stq-serif) !important; font-size: clamp(1.4rem, 4vw, 1.8rem); font-weight: 400; font-style: italic; line-height: 1.3; color: var(--stq-mid); margin-bottom: 32px; }
        .stq-intro-lead { font-size: clamp(1rem, 2.5vw, 1.15rem); line-height: 1.7; color: var(--stq-mid); margin-bottom: 24px; }
        .stq-intro-list { list-style: none; padding: 0; margin-bottom: 32px; }
        .stq-intro-list li { position: relative; padding-left: 20px; font-size: 0.95rem; color: var(--stq-mid); line-height: 1.8; }
        .stq-intro-list li::before { content: '\\00B7'; position: absolute; left: 4px; font-weight: 700; font-size: 1.4rem; color: var(--stq-accent); top: -2px; }
        .stq-intro-note { font-family: var(--stq-hand) !important; font-size: 1.15rem; color: var(--stq-accent); transform: rotate(-1.5deg); display: inline-block; margin-bottom: 32px; }
        .stq-intro-btns { display: flex; gap: 12px; flex-wrap: wrap; }
        @media (max-width: 600px) { .stq-intro-btns { flex-direction: column; } .stq-intro-btns .stq-btn { width: 100%; } }

        /* BUTTONS */
        .stq-btn { display: inline-block; font-family: var(--stq-sans) !important; font-size: 1rem; font-weight: 600; padding: 16px 36px; border-radius: 6px; text-decoration: none; cursor: pointer; transition: all 0.2s ease; border: none; text-align: center; -webkit-tap-highlight-color: transparent; }
        .stq-btn-primary { background: var(--stq-accent); color: #fff; }
        .stq-btn-primary:hover { background: #D14B08; transform: translateY(-1px); }
        .stq-btn-outline { background: transparent; color: var(--stq-text); border: 2px solid var(--stq-text); }
        .stq-btn-outline:hover { background: var(--stq-text); color: var(--stq-bg); }
        @media (max-width: 600px) { .stq-btn { padding: 14px 28px; font-size: 0.95rem; } }

        /* QUESTION SCREEN */
        .stq-q-fade { animation: stq-fadein 0.35s ease; }
        @keyframes stq-fadein { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        .stq-q-num { font-size: 0.75rem; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; color: var(--stq-accent); margin-bottom: 12px; }
        .stq-q-text { font-family: var(--stq-sans) !important; font-size: clamp(1.3rem, 4vw, 1.7rem); font-weight: 600; line-height: 1.4; letter-spacing: -0.01em; margin-bottom: 8px; }
        .stq-q-helper { font-size: 0.88rem; color: var(--stq-light); font-style: italic; margin-bottom: 28px; line-height: 1.6; }
        .stq-answers { display: flex; flex-direction: column; gap: 10px; margin-top: 20px; }
        .stq-answer { font-family: var(--stq-sans) !important; font-size: 0.95rem; font-weight: 500; padding: 16px 20px; background: var(--stq-card); border: 1px solid var(--stq-border); border-radius: 8px; cursor: pointer; transition: all 0.15s ease; text-align: left; color: var(--stq-text); -webkit-tap-highlight-color: transparent; }
        .stq-answer:hover { border-color: var(--stq-accent); background: #fff; }
        .stq-answer.stq-selected { border-color: var(--stq-accent); background: var(--stq-accent); color: #fff; }
        .stq-back { font-family: var(--stq-sans) !important; font-size: 0.85rem; font-weight: 600; color: var(--stq-light); background: none; border: none; cursor: pointer; margin-top: 24px; padding: 0; }
        .stq-back:hover { color: var(--stq-text); }

        /* EMAIL CAPTURE */
        .stq-email-title { font-family: var(--stq-sans) !important; font-size: clamp(1.3rem, 4vw, 1.6rem); font-weight: 700; margin-bottom: 8px; }
        .stq-email-sub { font-size: 0.95rem; color: var(--stq-mid); margin-bottom: 28px; }
        .stq-email-input { width: 100%; font-family: var(--stq-sans) !important; font-size: 1rem; padding: 14px 16px; border: 1px solid var(--stq-border); border-radius: 6px; background: var(--stq-card); color: var(--stq-text); outline: none; transition: border-color 0.15s; margin-bottom: 8px; }
        .stq-name-row { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
        @media (max-width: 480px) { .stq-name-row { grid-template-columns: 1fr; gap: 0; } }
        .stq-email-input:focus { border-color: var(--stq-accent); background: #fff; }
        .stq-email-error { font-size: 0.82rem; color: #D32F2F; margin-bottom: 16px; display: block; }

        /* LOADING */
        .stq-loading { text-align: center; }
        .stq-loading-stack { max-width: 460px; margin: 0 auto; padding: 0 16px; }
        .stq-loading-text { font-family: var(--stq-serif) !important; font-size: clamp(1.2rem, 3.4vw, 1.5rem); font-style: italic; color: var(--stq-text); line-height: 1.4; min-height: 2.2em; }
        .stq-loading-sub { font-family: var(--stq-sans) !important; font-size: 0.9rem; color: var(--stq-light); margin-top: 10px; line-height: 1.55; min-height: 2.6em; }
        .stq-loading-fade { animation: stq-loading-fade 1.1s ease; }
        @keyframes stq-loading-fade {
          0% { opacity: 0; transform: translateY(4px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .stq-loading-progress { display: flex; justify-content: center; gap: 8px; margin-top: 28px; }
        .stq-loading-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--stq-border); transition: background-color 0.9s ease, transform 0.9s ease; }
        .stq-loading-dot-active { background: var(--stq-accent); transform: scale(1.15); }
        .stq-spinner { width: 40px; height: 40px; border: 3px solid var(--stq-border); border-top-color: var(--stq-accent); border-radius: 50%; animation: stq-spin 0.8s linear infinite; margin: 24px auto; }
        @keyframes stq-spin { to { transform: rotate(360deg); } }

        /* RESULTS */
        .stq-results { padding: 48px 0 96px; }
        @media (max-width: 600px) { .stq-results { padding: 32px 0 64px; } }
        .stq-score-display { text-align: center; margin-bottom: 40px; }
        .stq-score-label { font-size: 0.75rem; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; color: var(--stq-light); margin-bottom: 8px; }
        .stq-score-number { font-size: clamp(4rem, 15vw, 7rem); font-weight: 700; line-height: 1; letter-spacing: -0.04em; color: var(--stq-text); }
        .stq-score-band { font-family: var(--stq-serif) !important; font-size: clamp(1.3rem, 4vw, 1.8rem); font-style: italic; color: var(--stq-accent); margin-top: 4px; }
        .stq-archetype { font-size: 0.82rem; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: var(--stq-mid); margin-top: 16px; }
        .stq-summary { font-size: clamp(1rem, 2.5vw, 1.1rem); line-height: 1.7; color: var(--stq-mid); text-align: center; max-width: 600px; margin: 0 auto 48px; }

        /* ARCHETYPE CARD */
        .stq-archetype-card {
          background: var(--stq-card);
          border: 1px solid var(--stq-border);
          border-radius: 12px;
          padding: 32px 28px 28px;
          margin: 8px 0 48px;
          text-align: center;
          position: relative;
        }
        @media (max-width: 600px) { .stq-archetype-card { padding: 24px 20px 22px; margin-bottom: 36px; } }
        .stq-archetype-eyebrow {
          font-size: 0.72rem; font-weight: 700; letter-spacing: 0.18em; text-transform: uppercase;
          color: var(--stq-accent); margin-bottom: 16px;
        }
        .stq-archetype-icon-wrap {
          display: flex; justify-content: center; margin: 0 auto 20px;
          width: 180px; height: 180px;
        }
        @media (max-width: 600px) { .stq-archetype-icon-wrap { width: 140px; height: 140px; } }
        .stq-archetype-icon-wrap > div { width: 100%; height: 100%; }
        .stq-archetype-icon-wrap svg { width: 100%; height: 100%; }
        .stq-archetype-name {
          font-family: var(--stq-sans) !important;
          font-size: clamp(1.6rem, 4.5vw, 2.2rem);
          font-weight: 700; letter-spacing: -0.02em;
          line-height: 1.15; color: var(--stq-text); margin-bottom: 6px;
        }
        .stq-archetype-tagline {
          font-family: var(--stq-serif) !important;
          font-size: 1rem; font-style: italic;
          color: var(--stq-mid); margin-bottom: 20px;
        }
        .stq-archetype-diagnosis {
          font-size: clamp(1.05rem, 2.6vw, 1.2rem);
          line-height: 1.55; font-weight: 500;
          color: var(--stq-text); margin: 0 auto 14px;
          max-width: 560px;
        }
        .stq-archetype-summary {
          font-size: 0.95rem; line-height: 1.7;
          color: var(--stq-mid); margin: 0 auto 24px;
          max-width: 560px;
        }
        .stq-downstream-chip {
          display: inline-flex; align-items: center; gap: 10px;
          padding: 8px 16px;
          background: #fff;
          border: 1px solid var(--stq-border);
          border-radius: 999px;
          margin-top: 8px;
        }
        .stq-downstream-label {
          font-size: 0.7rem; font-weight: 700; letter-spacing: 0.12em;
          text-transform: uppercase; color: var(--stq-light);
        }
        .stq-downstream-value {
          font-size: 0.85rem; font-weight: 600;
          padding: 2px 10px; border-radius: 999px;
          color: #fff;
        }
        .stq-downstream-low { background: #4CAF50; }
        .stq-downstream-moderate { background: #FF9800; }
        .stq-downstream-high { background: #D32F2F; }

        .stq-diagnosis-line { font-family: var(--stq-serif) !important; font-size: clamp(1.2rem, 3.5vw, 1.5rem); font-style: italic; line-height: 1.5; color: var(--stq-text); text-align: center; max-width: 560px; margin: 0 auto 16px; }

        .stq-drivers-block { margin-bottom: 8px; }
        .stq-drivers-intro { font-size: 0.93rem; color: var(--stq-mid); margin-bottom: 12px; line-height: 1.6; }
        .stq-drivers-list { list-style: none; padding: 0; }
        .stq-drivers-list li { position: relative; padding: 10px 0 10px 20px; font-size: 0.93rem; font-weight: 500; color: var(--stq-text); border-bottom: 1px solid var(--stq-border); line-height: 1.5; }
        .stq-drivers-list li:last-child { border-bottom: none; }
        .stq-drivers-list li::before { content: '\\2192'; position: absolute; left: 0; color: var(--stq-accent); font-weight: 700; }

        .stq-cost-grid { display: grid; gap: 12px; }
        .stq-metric-card { background: var(--stq-card); border-radius: 10px; padding: 24px; }
        .stq-metric-label { font-size: 0.72rem; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; color: var(--stq-accent); margin-bottom: 4px; }
        .stq-metric-value { font-size: clamp(1.6rem, 5vw, 2.2rem); font-weight: 700; letter-spacing: -0.02em; line-height: 1.2; margin-bottom: 10px; }
        .stq-metric-support { font-size: 0.9rem; color: var(--stq-mid); line-height: 1.6; margin-bottom: 8px; }
        .stq-metric-detail { font-size: 0.82rem; color: var(--stq-light); line-height: 1.6; margin-bottom: 4px; }
        .stq-metric-note { font-size: 0.78rem; font-weight: 600; font-style: italic; color: var(--stq-accent); }

        .stq-card-diagnosis { font-weight: 600; font-size: 0.95rem; line-height: 1.5; margin-bottom: 8px; color: var(--stq-text); }
        .stq-card-evidence { font-size: 0.88rem; color: var(--stq-mid); line-height: 1.6; margin-bottom: 6px; font-style: italic; }
        .stq-card-cost { font-size: 0.88rem; color: var(--stq-mid); line-height: 1.6; }

        .stq-sections-header { font-size: 0.72rem; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; color: var(--stq-light); margin-bottom: 16px; }
        .stq-section-card { background: var(--stq-card); border-radius: 10px; padding: 24px; margin-bottom: 12px; }
        .stq-section-card-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
        .stq-section-name { font-weight: 600; font-size: 0.95rem; }
        .stq-section-score-pill { font-size: 0.72rem; font-weight: 700; letter-spacing: 0.06em; padding: 3px 10px; border-radius: 4px; color: #fff; }
        .stq-section-bar-track { height: 4px; background: var(--stq-border); border-radius: 2px; overflow: hidden; margin-bottom: 10px; }
        .stq-section-bar-fill { height: 100%; border-radius: 2px; transition: width 0.6s ease; }
        .stq-section-insight { font-size: 0.9rem; color: var(--stq-mid); line-height: 1.6; }

        .stq-cost-block { margin: 40px 0; padding: 28px; border-left: 3px solid var(--stq-accent); background: var(--stq-card); border-radius: 0 10px 10px 0; }
        .stq-cost-label { font-size: 0.72rem; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; color: var(--stq-accent); margin-bottom: 8px; }
        .stq-cost-text { font-size: 0.95rem; color: var(--stq-mid); line-height: 1.65; }
        .stq-cost-text + .stq-cost-text { margin-top: 14px; }

        .stq-cta-block { text-align: center; margin-top: 48px; padding: 40px 0; border-top: 1px solid var(--stq-border); }
        .stq-cta-headline { font-family: var(--stq-sans) !important; font-size: clamp(1.2rem, 3.5vw, 1.5rem); font-weight: 700; margin-bottom: 12px; }
        .stq-cta-text { font-size: 0.95rem; color: var(--stq-mid); margin-bottom: 24px; max-width: 520px; margin-left: auto; margin-right: auto; line-height: 1.65; }
      `}</style>

      {/* ============ INTRO ============ */}
      {screen === 'intro' && (
        <div className="stq-container">
          <div className="stq-screen stq-intro" data-testid="quiz-intro">
            <span className="stq-intro-label">Diagnostic</span>
            <h1>The Success Tax</h1>
            <h2>The hidden cost of carrying it all</h2>
            <p className="stq-intro-lead">This is a fast diagnostic for leaders, founders, and high-responsibility people. It measures the hidden cost of:</p>
            <ul className="stq-intro-list">
              <li>constant responsiveness</li>
              <li>fragmented attention</li>
              <li>weak recovery</li>
              <li>boundary collapse</li>
              <li>carrying more than your system can metabolize</li>
            </ul>
            <p className="stq-intro-lead">16 questions. About 3 minutes.</p>
            <p className="stq-intro-note">If even 3 minutes feels hard to make space for, that's probably the cost showing up in real time.</p>
            <div className="stq-intro-btns">
              <button className="stq-btn stq-btn-primary" onClick={() => { setScreen('quiz'); setCurrentQ(0); }} data-testid="start-quiz-btn">Take the Quiz</button>
              <button className="stq-btn stq-btn-outline" onClick={() => navigate('/ctfdprotocol')} data-testid="skip-quiz-btn">Skip the Quiz and Apply for CTFD</button>
            </div>
          </div>
        </div>
      )}

      {/* ============ QUESTION ============ */}
      {screen === 'quiz' && (
        <div className="stq-container">
          <div className="stq-progress">
            <div className="stq-progress-track"><div className="stq-progress-fill" style={{ width: `${((currentQ + 1) / 16) * 100}%` }} /></div>
            <div className="stq-progress-label">{currentQ + 1} of 16</div>
          </div>
          <div className="stq-screen stq-q-fade" key={fadeKey} data-testid={`question-${currentQ}`}>
            <div>
              <div className="stq-q-num">Question {currentQ + 1}</div>
              <div className="stq-q-text">{QUESTIONS[currentQ].text}</div>
              {QUESTIONS[currentQ].helper && <div className="stq-q-helper">{QUESTIONS[currentQ].helper}</div>}
              {QUESTIONS[currentQ].type === 'numeric' ? (
                <div className="stq-numeric-block">
                  <input
                    type="number"
                    inputMode="numeric"
                    min="0"
                    step="1"
                    className="stq-email-input"
                    placeholder={QUESTIONS[currentQ].placeholder || '0'}
                    value={downstreamInput}
                    onChange={e => { setDownstreamInput(e.target.value); setDownstreamError(''); }}
                    onKeyDown={e => e.key === 'Enter' && submitDownstream()}
                    data-testid={`numeric-input-${currentQ}`}
                    autoFocus
                  />
                  {downstreamError && <span className="stq-email-error">{downstreamError}</span>}
                  <button className="stq-btn stq-btn-primary" style={{ width: '100%', marginTop: 12 }} onClick={submitDownstream} data-testid={`numeric-next-${currentQ}`}>Continue</button>
                </div>
              ) : (
                <div className="stq-answers">
                  {QUESTIONS[currentQ].answers.map((a, ai) => (
                    <button key={ai} className={`stq-answer${answers[currentQ] === ai ? ' stq-selected' : ''}`}
                      onClick={() => selectAnswer(currentQ, ai)} data-testid={`answer-${currentQ}-${ai}`}>{a}</button>
                  ))}
                </div>
              )}
              <button className="stq-back" onClick={goBack} data-testid="quiz-back-btn">&larr; Back</button>
            </div>
          </div>
        </div>
      )}

      {/* ============ Q16 OPEN-TEXT (REQUIRED) ============ */}
      {screen === 'q16' && (
        <div className="stq-container">
          <div className="stq-progress">
            <div className="stq-progress-track"><div className="stq-progress-fill" style={{ width: '100%' }} /></div>
            <div className="stq-progress-label">Question 16 of 16</div>
          </div>
          <div className="stq-screen stq-q-fade" data-testid="question-16">
            <div>
              <div className="stq-q-num">Question 16</div>
              <div className="stq-q-text">If calming your system freed up meaningful time each week without costing you performance, where would you want that time to go first?</div>
              <div className="stq-q-helper">Examples: more time with my kids, my partner, rowing, workouts, music, rest, reading, being outside, deep work, calling friends, or simply having more space</div>
              <textarea
                className="stq-email-input"
                rows={4}
                placeholder="e.g. more time with my son / more time rowing / more time to rest / more time for music"
                value={personalTime}
                onChange={e => { setPersonalTime(e.target.value); setPersonalTimeError(''); }}
                data-testid="personal-time-input"
                autoFocus
                style={{ resize: 'vertical', minHeight: 100 }}
              />
              {personalTimeError && <span className="stq-email-error">{personalTimeError}</span>}
              <button
                className="stq-btn stq-btn-primary"
                style={{ width: '100%', marginTop: 12 }}
                onClick={() => {
                  const trimmed = personalTime.trim();
                  if (trimmed.length < 3) {
                    setPersonalTimeError('Please share where that time would go (a few words is enough)');
                    return;
                  }
                  setPersonalTimeError('');
                  setScreen('email');
                }}
                data-testid="q16-continue-btn"
              >Continue</button>
              <button className="stq-back" onClick={() => { setFadeKey(k => k + 1); setScreen('quiz'); setCurrentQ(14); }} data-testid="q16-back-btn">&larr; Back</button>
            </div>
          </div>
        </div>
      )}

      {/* ============ EMAIL CAPTURE ============ */}
      {screen === 'email' && (
        <div className="stq-container">
          <div className="stq-screen" data-testid="email-capture">
            <div>
              <div className="stq-email-title">Where should we send your results?</div>
              <p className="stq-email-sub">Enter your name, email, and website or LinkedIn to see your full Success Tax breakdown, archetype, and personalized interpretation.</p>
              <div className="stq-name-row">
                <input
                  type="text"
                  className="stq-email-input"
                  placeholder="First name"
                  value={firstName}
                  onChange={e => { setFirstName(e.target.value); setNameError(''); }}
                  onKeyDown={e => e.key === 'Enter' && submitQuiz()}
                  data-testid="quiz-first-name-input"
                  autoFocus
                  autoComplete="given-name"
                />
                <input
                  type="text"
                  className="stq-email-input"
                  placeholder="Last name"
                  value={lastName}
                  onChange={e => { setLastName(e.target.value); setNameError(''); }}
                  onKeyDown={e => e.key === 'Enter' && submitQuiz()}
                  data-testid="quiz-last-name-input"
                  autoComplete="family-name"
                />
              </div>
              {nameError && <span className="stq-email-error" data-testid="name-error">{nameError}</span>}
              <input
                type="email"
                className="stq-email-input"
                placeholder="you@example.com"
                value={email}
                onChange={e => { setEmail(e.target.value); setEmailError(''); }}
                onKeyDown={e => e.key === 'Enter' && submitQuiz()}
                data-testid="quiz-email-input"
                autoComplete="email"
                style={{ marginTop: 10 }}
              />
              {emailError && <span className="stq-email-error">{emailError}</span>}
              <input
                type="url"
                className="stq-email-input"
                placeholder="Your website or LinkedIn URL"
                value={profileUrl}
                onChange={e => { setProfileUrl(e.target.value); setProfileUrlError(''); }}
                onKeyDown={e => e.key === 'Enter' && submitQuiz()}
                data-testid="quiz-profile-url-input"
                autoComplete="url"
                style={{ marginTop: 10 }}
              />
              {profileUrlError && <span className="stq-email-error" data-testid="profile-url-error">{profileUrlError}</span>}
              <button className="stq-btn stq-btn-primary" style={{ width: '100%', marginTop: 12 }} onClick={submitQuiz} data-testid="quiz-see-results-btn">See My Results</button>
              <button className="stq-back" onClick={() => setScreen('q16')} data-testid="email-back-btn" style={{ marginTop: 16 }}>&larr; Back</button>
            </div>
          </div>
        </div>
      )}

      {/* ============ LOADING ============ */}
      {screen === 'loading' && (
        <div className="stq-container">
          <div className="stq-screen stq-loading" data-testid="quiz-loading">
            <div>
              <div className="stq-spinner" />
              <div className="stq-loading-stack">
                <div key={`m-${loadingStep}`} className="stq-loading-text stq-loading-fade" data-testid="loading-main">
                  {LOADING_STEPS[loadingStep].main}
                </div>
                <div key={`s-${loadingStep}`} className="stq-loading-sub stq-loading-fade" data-testid="loading-sub">
                  {LOADING_STEPS[loadingStep].sub}
                </div>
                <div className="stq-loading-progress" aria-hidden="true">
                  {LOADING_STEPS.map((_, i) => (
                    <span key={i} className={`stq-loading-dot${i <= loadingStep ? ' stq-loading-dot-active' : ''}`} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============ RESULTS ============ */}
      {screen === 'results' && results && (
        <div className="stq-container">
          <div className="stq-results" data-testid="quiz-results">
            {/* 1. Score */}
            <div className="stq-score-display">
              <div className="stq-score-label">Your Success Tax Score</div>
              <div className="stq-score-number">{results.overallScore}</div>
              <div className="stq-score-band">{results.overallBand}</div>
            </div>

            {/* 2. Archetype Card */}
            <div className="stq-archetype-card" data-testid="archetype-card">
              <div className="stq-archetype-eyebrow">Your Archetype</div>
              <div className="stq-archetype-icon-wrap" data-testid="archetype-icon">
                <ArchetypeIcon name={results.archetype} size={180} />
              </div>
              <div className="stq-archetype-name" data-testid="archetype-name">{results.archetype}</div>
              <div className="stq-archetype-tagline">{ARCHETYPES[results.archetype]?.tagline}</div>
              <p className="stq-archetype-diagnosis">{insights?.diagnosisLine || ARCHETYPES[results.archetype]?.diagnosis}</p>
              <p className="stq-archetype-summary">{insights?.summary || ARCHETYPES[results.archetype]?.summary}</p>
              <div className="stq-downstream-chip" data-testid="downstream-impact">
                <span className="stq-downstream-label">Downstream Impact</span>
                <span className={`stq-downstream-value stq-downstream-${results.downstreamImpact?.toLowerCase()}`}>{results.downstreamImpact}</span>
              </div>
            </div>

            {/* 2. What Pushed Your Score Up */}
            {derived?.topDrivers?.length > 0 && (
              <div className="stq-drivers-block" data-testid="top-drivers">
                <div className="stq-sections-header">What pushed your score up</div>
                {insights?.topDriversIntro && <p className="stq-drivers-intro">{insights.topDriversIntro}</p>}
                <ul className="stq-drivers-list">
                  {derived.topDrivers.map((d, i) => <li key={i}>{d}</li>)}
                </ul>
              </div>
            )}

            {/* 3. What You Could Get Back */}
            <div className="stq-sections-header" style={{ marginTop: 40 }}>What you could get back</div>
            <div className="stq-cost-grid" data-testid="reclaim-metrics">
              {/* Time You Could Get Back */}
              <div className="stq-metric-card" data-testid="time-reclaimed">
                <div className="stq-metric-label">Time You Could Get Back</div>
                <div className="stq-metric-value">~{derived?.timeReclaimed?.range || '0 hrs/week'}</div>
                {derived?.leanWorkweek && (
                  <p className="stq-metric-support">Your current workload could likely look more like {derived.leanWorkweek.leanLow}–{derived.leanWorkweek.leanHigh} hours instead of {derived.leanWorkweek.workHours}.</p>
                )}
                {derived?.timeReclaimed && (
                  <p className="stq-metric-detail">Roughly {derived.timeReclaimed.monthlyLow}–{derived.timeReclaimed.monthlyHigh} hours a month, or {derived.timeReclaimed.yearlyDaysLow}–{derived.timeReclaimed.yearlyDaysHigh} full days a year.</p>
                )}
                <p className="stq-metric-note">Directional estimate, not a promise.</p>
              </div>

              {/* What This Could Reopen */}
              <div className="stq-metric-card" data-testid="what-this-could-reopen">
                <div className="stq-metric-label">What this could reopen</div>
                {insights?.reopenSentence ? (
                  <div className="stq-metric-value" style={{ fontSize: 'clamp(1.2rem, 3.2vw, 1.5rem)', lineHeight: 1.4 }}>
                    {insights.reopenSentence}
                  </div>
                ) : (
                  <div className="stq-metric-value" style={{ fontSize: 'clamp(1.2rem, 3.2vw, 1.5rem)', lineHeight: 1.4 }}>
                    What would it be worth to have {derived?.timeReclaimed?.yearlyDaysLow || 0}–{derived?.timeReclaimed?.yearlyDaysHigh || 0} more full days a year for {insights?.normalizedPersonalTimePhrase || 'rest, presence, or whatever matters most right now'}?
                  </div>
                )}
              </div>

              {/* Recovery Debt */}
              <div className="stq-metric-card" data-testid="recovery-debt">
                <div className="stq-metric-label">Recovery Debt</div>
                <div className="stq-metric-value">~{derived?.recoveryDebt?.debt || 0} hrs/week</div>
                <p className="stq-metric-support">This is the gap between the recovery your current load needs and what you're actually getting. It's the reason you can be productive and still feel depleted.</p>
                <p className="stq-metric-detail">Real recovery looks like: walking without input, breathwork, stillness, sauna, nature, journaling, device-free decompression. Scrolling and streaming don't count.</p>
                <p className="stq-metric-note">This time can come back as your attention leak shrinks and recovery gets a real structure.</p>
              </div>

              {/* Attention Leak */}
              <div className="stq-metric-card" data-testid="attention-leak">
                <div className="stq-metric-label">Attention Leak</div>
                <div className="stq-metric-value">{derived?.attentionLeak?.displayRange || '0 hrs/week'}</div>
                <p className="stq-metric-support">The weekly time your brain loses to switching, re-entry, and fragmented attention.</p>
                <p className="stq-metric-detail">Not the time you spend checking your phone — the time it takes your brain to get back into gear afterward.</p>
              </div>

              {/* Team Spillover Cost */}
              <div className="stq-metric-card" data-testid="team-spillover">
                <div className="stq-metric-label">Team Spillover Cost</div>
                <div className="stq-metric-value">~{derived?.teamSpillover?.teamSpilloverHours || 0} downstream hours/week</div>
                <p className="stq-metric-support">An estimate of how much of your attention and recovery drag is spreading through the {derived?.teamSpillover?.downstreamCount || 0} people whose pace depends on yours.</p>
                <p className="stq-metric-detail">Conservative estimate. Not a scientific measurement.</p>
              </div>
            </div>

            {/* 4. Section Cards */}
            <div className="stq-sections-header" style={{ marginTop: 40 }}>Where the cost is coming from</div>
            <SectionCard name="Load" score={results.sectionScores.load} label={results.sectionLabels.load} insight={insights?.sectionInsights?.load} />
            <SectionCard name="Recovery Gap" score={results.sectionScores.recoveryGap} label={results.sectionLabels.recoveryGap} insight={insights?.sectionInsights?.recoveryGap} />
            <SectionCard name="Fragmentation" score={results.sectionScores.fragmentation} label={results.sectionLabels.fragmentation} insight={insights?.sectionInsights?.fragmentation} />
            <SectionCard name="Boundary Collapse" score={results.sectionScores.boundaryCollapse} label={results.sectionLabels.boundaryCollapse} insight={insights?.sectionInsights?.boundaryCollapse} />
            <SectionCard name="Leadership Spillover" score={results.sectionScores.leadershipSpillover} label={results.sectionLabels.leadershipSpillover} insight={insights?.sectionInsights?.leadershipSpillover} />

            {/* 5. If Nothing Changes */}
            <div className="stq-cost-block" data-testid="if-nothing-changes">
              <div className="stq-cost-label">If Nothing Changes</div>
              <p className="stq-cost-text">{insights?.costOfInaction || "If nothing changes, you keep losing time. You keep losing peace. The thing you said you wanted more time for — you won't get to do it. And AI is going to make this rush bigger, not smaller."}</p>
              <p className="stq-cost-text" data-testid="procrastination-trap">{insights?.procrastinationTrap || "Most people read something like this, nod, and tell themselves they'll deal with it later. After the next launch. After the deal closes. After they sell the company. But \"later\" never comes. Meanwhile your real life is paying the bill, quietly, every week."}</p>
            </div>

            {/* 6. CTA */}
            <div className="stq-cta-block">
              <div className="stq-cta-headline">This Is Exactly What CTFD Is Built For</div>
              <p className="stq-cta-text">{insights?.ctaBridge || "CTFD is built to help you stop losing the things that actually matter — your time, your peace, the people and moments you wanted to be present for — without losing your edge."}</p>
              <button className="stq-btn stq-btn-primary" onClick={trackApply} data-testid="quiz-apply-btn">Apply for CTFD</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
